import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, MapPin } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, FadeIn } from "@/components/Reveal";
import { useSEO } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUEST_TYPES = [
    "Elaborazione dati contabili",
    "Attività amministrativa e di supporto",
    "Consulenza imprenditoriale e gestionale",
    "Bandi e finanza agevolata",
    "Informazioni generali",
    "Altro",
];

const INFO_ROWS = [
    { k: "SOCIETÀ", v: "Service & Business SRLS" },
    { k: "INDIRIZZO", v: "Corso Garibaldi 92, 86170 Isernia (IS)" },
    { k: "TELEFONO", v: <a href="tel:+393347562887" data-testid="contatti-tel-cell" className="text-white/75 hover:text-sky-300 transition-colors">+39 334 756 2887</a> },
    { k: "TEL. FISSO", v: <a href="tel:+390865220588" data-testid="contatti-tel-fisso" className="text-white/75 hover:text-sky-300 transition-colors">0865 220 588</a> },
    { k: "EMAIL", v: <a href="mailto:service.business.srls@gmail.com" data-testid="contatti-email" className="text-white/75 hover:text-sky-300 transition-colors break-all">service.business.srls@gmail.com</a> },
    { k: "PEC", v: <a href="mailto:service.business.srls@pec.it" data-testid="contatti-pec" className="text-white/75 hover:text-sky-300 transition-colors break-all">service.business.srls@pec.it</a> },
    { k: "P.IVA", v: "00999760945" },
];

const EMPTY = {
    nome: "",
    azienda: "",
    email: "",
    telefono: "",
    tipologia: "",
    messaggio: "",
    privacy: false,
    website: "",
};

const labelCls = "block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-2";

export default function Contatti() {
    useSEO(
        "Contatti | Service & Business SRLS",
        "Contatta Service & Business SRLS: supporto contabile, amministrativo e gestionale per imprese e professionisti. Sede a Isernia, operativi anche da remoto."
    );

    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.privacy) {
            toast.error("Devi accettare l'informativa privacy per inviare la richiesta.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form, { timeout: 30000 });
            toast.success("Richiesta inviata. Ti ricontatteremo al più presto.");
            setForm(EMPTY);
        } catch {
            toast.error("Invio non riuscito. Riprova tra poco.");
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <PageHero
                eyebrow="Contatti"
                lines={["Parliamo", "della tua attività."]}
                sub="Hai bisogno di supporto contabile, amministrativo, gestionale o vuoi capire quali opportunità possono essere disponibili per la tua impresa? Raccontaci la tua esigenza."
            />

            <section className="pb-8">
                <div className="wrap grid lg:grid-cols-[0.85fr_1.15fr] gap-10 pb-10">
                    {/* INFO */}
                    <FadeIn delay={0.2}>
                        <aside className="card-dark rounded-3xl p-8 lg:sticky lg:top-28" data-testid="contatti-info-panel">
                            <p className="font-mono text-[10px] tracking-[0.28em] text-sky-400 uppercase">Recapiti</p>
                            <ul className="mt-7 space-y-4">
                                {INFO_ROWS.map((r) => (
                                    <li key={r.k} className="flex items-baseline gap-4 text-sm">
                                        <span className="font-mono text-[9px] tracking-[0.2em] text-white/35 w-[76px] shrink-0">{r.k}</span>
                                        <span className="text-white/75">{r.v}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-8 pt-8 border-t border-white/8 flex items-start gap-3">
                                <MapPin className="h-4 w-4 text-sky-400 mt-0.5 shrink-0" />
                                <p className="text-xs text-slate-400 leading-relaxed">
                                    Radicati a Isernia, operativi anche completamente da remoto: seguiamo clienti anche
                                    al di fuori del nostro territorio.
                                </p>
                            </div>
                        </aside>
                    </FadeIn>

                    {/* FORM */}
                    <Reveal delay={0.1}>
                        <form onSubmit={submit} className="card-dark rounded-3xl p-8 sm:p-10" data-testid="contact-form" noValidate={false}>
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="nome" className={labelCls}>Nome e cognome *</label>
                                    <input id="nome" type="text" required value={form.nome} onChange={set("nome")} data-testid="contact-form-name-input" className="field" placeholder="Il tuo nome" />
                                </div>
                                <div>
                                    <label htmlFor="azienda" className={labelCls}>Azienda / attività</label>
                                    <input id="azienda" type="text" value={form.azienda} onChange={set("azienda")} data-testid="contact-form-company-input" className="field" placeholder="Denominazione dell'attività" />
                                </div>
                                <div>
                                    <label htmlFor="email" className={labelCls}>Email *</label>
                                    <input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-form-email-input" className="field" placeholder="nome@azienda.it" />
                                </div>
                                <div>
                                    <label htmlFor="telefono" className={labelCls}>Telefono</label>
                                    <input id="telefono" type="tel" value={form.telefono} onChange={set("telefono")} data-testid="contact-form-phone-input" className="field" placeholder="+39 ..." />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="tipologia" className={labelCls}>Tipologia di richiesta *</label>
                                    <select id="tipologia" required value={form.tipologia} onChange={set("tipologia")} data-testid="contact-form-service-select" className="field">
                                        <option value="" disabled>Seleziona tipologia di richiesta...</option>
                                        {REQUEST_TYPES.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="messaggio" className={labelCls}>Messaggio *</label>
                                    <textarea id="messaggio" required rows={5} value={form.messaggio} onChange={set("messaggio")} data-testid="contact-form-message-input" className="field resize-none" placeholder="Raccontaci la tua esigenza" />
                                </div>
                            </div>

                            <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                            <label className="flex items-start gap-3 mt-6 cursor-pointer" htmlFor="privacy">
                                <input id="privacy" type="checkbox" checked={form.privacy} onChange={set("privacy")} data-testid="contact-form-privacy-checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-sky-500" />
                                <span className="text-xs text-slate-400 leading-relaxed">
                                    Dichiaro di aver letto l'<a href="/privacy-policy" className="text-sky-400 underline underline-offset-2">informativa privacy</a> e acconsento al trattamento dei miei dati personali per essere ricontattato. *
                                </span>
                            </label>

                            <button type="submit" disabled={sending} data-testid="contact-form-submit-button" className="btn-primary mt-8 w-full sm:w-auto disabled:opacity-60 disabled:pointer-events-none">
                                {sending ? "INVIO IN CORSO..." : "INVIA RICHIESTA"}
                                {!sending && <ArrowRight className="h-4 w-4" />}
                            </button>
                        </form>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
